import React, { useState, useRef, useCallback, useEffect } from 'react';
import { shallow } from 'zustand/shallow';
import { useClockTick } from '../../contexts/ClockTickContext.jsx';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cuesAPI, singularAPI } from '../../lib/api';
import { useShowStore } from '../../stores/showStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { useSettingsStore, effectiveRelativeTimes } from '../../stores/settingsStore';
import CueTypeBadge, { TYPE_META } from './CueTypeBadge';
import InTime from './InTime';
import VmixStepForm from './VmixStepForm';
import { noMouseFocus } from './dragHandle';

// ── Cue-type accents ──────────────────────────────────────────────────────────
// ONE entry per cue type drives all three treatments below (label-box outline,
// resting label fill, row hover tint). They used to be three hand-maintained
// maps, which is how CAMERA and VT both ended up on green-400 — indistinguishable
// at a glance. Deriving them means a type's colour can only be changed in one
// place. Families track the --t-* tokens in index.css, which colour the type
// chip; keep the two in step when adding a type.
//
//   border = null → no label outline (SEGMENT is the "cruise through" default
//   type and must not pull the eye). bg = null → no resting fill either.
//
// Class names are written out in full so Tailwind's scanner can see them.
// Families mirror the --t-* tokens: greens are playout, blues are camera.
const CUE_TYPE_ACCENT = {
  SEGMENT:              { border: null,                 bg: null,                hover: 'hover:bg-app-row-hover' },
  // ── playout (green). VT and VT VOX share one green on purpose: same action,
  //    told apart by the badge text, which leaves replay the whole lime end ──
  VT:                   { border: 'border-green-400',   bg: 'bg-green-900/30',   hover: 'hover:bg-green-900/30' },
  VT_VOX:               { border: 'border-green-400',   bg: 'bg-green-900/30',   hover: 'hover:bg-green-900/30' },
  REPLAY:               { border: 'border-lime-400',    bg: 'bg-lime-900/30',    hover: 'hover:bg-lime-900/30' },
  // ── camera (blue). The -300 tint rather than -400: blue reads much darker
  //    than green at equal lightness, and blue-300 (#93c5fd) lands on VT's
  //    perceived brightness. Fills use /30 to match VT's too ──
  CAMERA:               { border: 'border-blue-300',    bg: 'bg-blue-900/30',    hover: 'hover:bg-blue-900/30' },
  // ── presenters: pink, not a second blue. At equal brightness two blues could
  //    differ only in hue and read as one colour — see the token comment ──
  PRESENTERS_TO_CAMERA: { border: 'border-pink-300',    bg: 'bg-pink-900/30',    hover: 'hover:bg-pink-900/30' },
  // ── graphics / structure / other ──
  GRAPHIC:              { border: 'border-purple-500',  bg: 'bg-purple-900/30',  hover: 'hover:bg-purple-900/30' },
  AUDIO:                { border: 'border-fuchsia-400', bg: 'bg-fuchsia-900/25', hover: 'hover:bg-fuchsia-900/25' },
  PRE_SHOW:             { border: 'border-yellow-300',  bg: 'bg-yellow-800/30',  hover: 'hover:bg-yellow-800/30' },
  // ── retired: no longer offered when creating a cue, kept so a legacy or
  //    soft-deleted row still renders rather than losing its accent. NOTE is
  //    also CueTypeBadge's unknown-type fallback. BREAK is the ad-break type —
  //    NOT SECTION_BREAK, which is very much still in use ──
  RECORD_STREAM:        { border: 'border-red-400',     bg: 'bg-red-900/25',     hover: 'hover:bg-red-900/25' },
  NOTE:                 { border: 'border-gray-600',    bg: 'bg-app-row-hover',  hover: 'hover:bg-app-row-hover' },
  BREAK:                { border: 'border-zinc-300',    bg: 'bg-zinc-800/40',    hover: 'hover:bg-zinc-800/40' },
};

const accents = Object.entries(CUE_TYPE_ACCENT);

// Type-coloured row hover hint (the row itself stays neutral at rest). The
// unmistakable type signature lives on the Label box outline instead — keeps the
// dense rundown calm but gives every row a coloured "tag" around the cue's
// most-read element. Leading space: it is concatenated onto a class string.
const CUE_TYPE_ROW_TINT = Object.fromEntries(accents.map(([type, a]) => [type, ` ${a.hover}`]));

// 2px rounded outline drawn around the Label cell only. Brighter than a
// row-level stripe and constrained to the cue's identifying text.
const CUE_TYPE_LABEL_OUTLINE = Object.fromEntries(
  accents.filter(([, a]) => a.border).map(([type, a]) => [type, `border-2 ${a.border} rounded-md`])
);

// Resting fill INSIDE the label box — the same tint the row picks up on hover.
// The box is therefore always tinted; on hover `group-hover:bg-transparent`
// drops the box's own fill so the row-level tint shows through uniformly and
// appears to spread to the rest of the row rather than doubling up behind it.
const CUE_TYPE_LABEL_FILL = Object.fromEntries(
  accents.filter(([, a]) => a.bg).map(([type, a]) => [type, a.bg])
);
import ScriptEditor from './ScriptEditor';

// Strip HTML tags for plain-text preview
function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

// Alphabetical for the inline type dropdown. RECORD_STREAM, NOTE and BREAK are
// retired (see CUE_TYPE_ACCENT above) so they are not offered here either.
const CUE_TYPES = ['AUDIO', 'CAMERA', 'GRAPHIC', 'GROUP', 'PRE_SHOW', 'PRESENTERS_TO_CAMERA', 'REPLAY', 'SECTION_BREAK', 'SEGMENT', 'SHOW_START', 'VT', 'VT_VOX'];

const SINGULAR_FIXED_FUNCTIONS = ['IN', 'OUT', 'hide_all'];

// The cue fields a guest without production access may edit — mirrors
// TEXT_FIELDS in backend/src/lib/access.js, which is what actually enforces it.
const TEXT_FIELDS = ['script', 'notes', 'top_right_logo', 'bottom_right_logo', 'other_graphics'];

// Format seconds as HH:MM:SS
function formatHMS(seconds) {
  if (seconds == null || isNaN(seconds)) return '--:--:--';
  const abs = Math.abs(Math.round(seconds));
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Live +/- cell — subscribes to shared tick context (one interval for all rows)
const LivePlusMinusCell = React.memo(function LivePlusMinusCell({ inTime }) {
  useClockTick(); // drives re-render every second via shared context
  const diffSeconds = Math.round((inTime - Date.now()) / 1000);
  const sign = diffSeconds >= 0 ? '+' : '-';
  const cls = diffSeconds >= 0 ? 'text-green-500 font-mono text-[14px]' : 'text-red-500 font-mono text-[14px]';
  return <span className={cls}>{sign}{formatHMS(Math.abs(diffSeconds))}</span>;
});

// Self-clocking +/- offset cell — only live cells subscribe to tick context.
// Dimmed to 55% on history rows so drift reads quietly once a cue is done.
const PlusMinusCell = React.memo(function PlusMinusCell({ inTime, status }) {
  const active = !!(inTime && status !== 'complete' && status !== 'skipped');
  const histCls = (status === 'complete' || status === 'skipped') ? ' opacity-[.55]' : '';
  if (!inTime) return <span className={`text-gray-600 font-mono text-[14px]${histCls}`}>--:--:--</span>;
  if (active) return <LivePlusMinusCell inTime={inTime} />;
  // Inactive (complete/skipped): show static value, no live update needed
  const diffSeconds = Math.round((inTime - Date.now()) / 1000);
  const sign = diffSeconds >= 0 ? '+' : '-';
  const cls = diffSeconds >= 0 ? 'text-green-500 font-mono text-[14px]' : 'text-red-500 font-mono text-[14px]';
  return <span className={`${cls}${histCls}`}>{sign}{formatHMS(Math.abs(diffSeconds))}</span>;
});

// Parse HH:MM:SS or MM:SS or raw seconds string → integer seconds
function parseHMS(str) {
  if (!str) return 0;
  const parts = str.trim().split(':').map(n => parseInt(n) || 0);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parseInt(str) || 0;
}

// Format a Date as HH:MM:SS wall clock
function formatClockTime(date) {
  if (!date) return '--:--:--';
  const h = date.getHours();
  const m = date.getMinutes();
  const s = date.getSeconds();
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// `multiline` renders a textarea that grows to fit its content. Enter still
// commits (so the muscle memory of type-label-Enter is unchanged) and
// Shift+Enter inserts a newline. Arrow up/down keep moving between rows, but
// only from the first/last line, so they can still navigate within a wrapped
// label instead of jumping away mid-edit.
function EditableCell({ value, onSave, placeholder, className, style, multiline = false, labelIndex, durationIndex, onArrowDown, onArrowUp, triggerData = {} }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  // Match the textarea's height to its content so the row grows as lines are
  // added. Runs on every draft change, and once on open.
  const autoGrow = useCallback(() => {
    const el = inputRef.current;
    if (!el || !multiline) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [multiline]);

  // Measure once now and again next frame: on the first render after opening,
  // the textarea's width inside its flex parent has not settled, so an
  // immediate measure sees the text wrapped and over-reports the height.
  useEffect(() => {
    if (!editing) return;
    autoGrow();
    const raf = requestAnimationFrame(autoGrow);
    return () => cancelAnimationFrame(raf);
  }, [editing, draft, autoGrow]);

  function startEdit() {
    setDraft(value || '');
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }

  function save() {
    setEditing(false);
    // Trim the ends but keep interior newlines — they are the whole point of
    // multiline. Also drop trailing spaces on each line so stray whitespace
    // doesn't change the rendered height.
    const trimmed = multiline
      ? draft.split('\n').map(l => l.replace(/\s+$/, '')).join('\n').trim()
      : draft.trim();
    if (trimmed !== (value || '').toString()) {
      onSave(trimmed);
    }
  }

  // In a textarea, only hand Arrow up/down to row navigation when the caret is
  // already on the first/last line; otherwise let it move within the text.
  function atEdge(dir) {
    const el = inputRef.current;
    if (!multiline || !el) return true;
    const pos = el.selectionStart;
    if (el.selectionStart !== el.selectionEnd) return false;
    return dir === 'up'
      ? !draft.slice(0, pos).includes('\n')
      : !draft.slice(pos).includes('\n');
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      // Shift+Enter = newline (multiline only); plain Enter always commits.
      if (multiline && e.shiftKey) {
        e.stopPropagation();
        return;
      }
      e.preventDefault();
      save();
    }
    if (e.key === 'Escape') {
      setEditing(false);
    }
    if (e.key === 'ArrowDown' && onArrowDown && atEdge('down')) {
      e.preventDefault();
      save();
      onArrowDown();
    }
    if (e.key === 'ArrowUp' && onArrowUp && atEdge('up')) {
      e.preventDefault();
      save();
      onArrowUp();
    }
    e.stopPropagation();
  }

  if (editing) {
    const editorCls = `bg-blue-900/20 border border-blue-600 rounded px-2 py-1 text-base text-white outline-none w-full ${className}`;
    if (multiline) {
      return (
        <textarea
          ref={inputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={handleKeyDown}
          onInput={autoGrow}
          rows={1}
          // whitespace-pre-wrap + break-words so a long single line wraps rather
          // than scrolling sideways out of a fixed-width column.
          style={{ ...style, overflow: 'hidden', resize: 'none', whiteSpace: 'pre-wrap' }}
          className={`${editorCls} break-words`}
        />
      );
    }
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
        style={style}
        className={editorCls}
      />
    );
  }

  return (
    <span
      onClick={startEdit}
      data-cue-label-trigger={labelIndex ?? undefined}
      data-cue-duration-trigger={durationIndex ?? undefined}
      {...triggerData}
      style={style}
      className={`editable-cell cursor-text ${className} ${!value ? 'text-gray-700 italic' : ''}`}
    >
      {value || placeholder}
    </span>
  );
}

// Describes a single step in the cue_actions timeline.
// `cueLookup` is an optional Map<cueId, { label, displayIndex }> used to
// render Loop Control steps with the human-readable target cue.
function stepLabel(step, cueLookup, vmixInstances) {
  if (step.type === 'delay') {
    return `Delay: ${step.duration_ms || 0}ms`;
  }
  if (step.type === 'vmix') {
    const parts = [`vMix: ${step.function}`];
    if (step.input) parts.push(`In: ${step.input}`);
    if (step.value) parts.push(`→ ${step.value}`);
    if (step.target_instance_id) {
      const inst = vmixInstances?.find(i => i.id === step.target_instance_id);
      parts.push(`@ ${inst ? inst.label : 'unknown instance'}`);
    }
    return parts.join('  ');
  }
  if (step.type === 'singular') {
    const name = step.sub_composition_name || step.sub_composition_id || '?';
    if (step.function === 'hide_all') return `Singular: Hide All`;
    if (step.function === 'IN' || step.function === 'OUT') return `Singular: ${name} → ${step.function}`;
    return `Singular: ${name} → ${step.function} = "${step.value || ''}"`;
  }
  if (step.type === 'loop_control') {
    const action = step.action;
    if (action === 'stop_all_immediate') return `Loop Stop ALL!`;
    if (action === 'stop_all_graceful') return `Loop Stop ALL after iteration`;
    const target = cueLookup?.get(step.target_cue_id);
    const verbMap = {
      start: 'Start',
      start_bounded: 'Start',
      start_infinite: 'Start ∞',
      stop: 'Stop!',
      stop_immediate: 'Stop!',
      stop_graceful: 'Stop after iteration',
    };
    const verb = verbMap[action] || 'Loop';
    if (!target) return `Loop ${verb}: ⚠ deleted cue`;
    return `Loop ${verb}: cue ${target.displayIndex ?? '—'} (${target.label || 'untitled'})`;
  }
  return JSON.stringify(step);
}

function stepBadgeClass(step) {
  if (step.type === 'delay') return 'text-yellow-600';
  if (step.type === 'vmix') return 'text-blue-400';
  if (step.type === 'singular') return 'text-purple-400';
  if (step.type === 'loop_control') return 'text-amber-400';
  return 'text-gray-400';
}

// Singular step form
function SingularStepForm({ showId, draft, setDraft, singularCompositions }) {
  const [nodeOptions, setNodeOptions] = useState([]);
  const [loadingNodes, setLoadingNodes] = useState(false);

  // Cache of subCompositionId -> node array
  const nodeCache = useRef({});

  useEffect(() => {
    if (!draft.sub_composition_id) {
      setNodeOptions([]);
      return;
    }
    if (nodeCache.current[draft.sub_composition_id]) {
      setNodeOptions(nodeCache.current[draft.sub_composition_id]);
      return;
    }
    setLoadingNodes(true);
    singularAPI.nodes(showId, draft.sub_composition_id)
      .then(res => {
        // res.data is [{ id, title, type }]
        const fields = Array.isArray(res.data) ? res.data : [];
        nodeCache.current[draft.sub_composition_id] = fields;
        setNodeOptions(fields);
      })
      .catch(() => setNodeOptions([]))
      .finally(() => setLoadingNodes(false));
  }, [draft.sub_composition_id, showId]);

  const isFixedFn = ['IN', 'OUT', 'hide_all', ''].includes(draft.function);
  const selectedNode = isFixedFn ? null : nodeOptions.find(n => n.id === draft.function);
  const nodeType = selectedNode?.type?.toLowerCase(); // 'text' | 'number' | 'selection' | 'button' | 'checkbox' | 'json' | 'bool'

  // Mirror the selected field's type onto the draft so buildStep() can persist it
  // (buildStep lives in the parent component and can't see nodeType directly).
  // For checkbox/bool fields also seed the value to "true" — the dropdown shows
  // True by default (value={draft.value ?? 'true'}), but until the draft itself
  // holds a value buildStep drops it, so an untouched default never saved and
  // only False (which requires actively changing the dropdown) worked.
  useEffect(() => {
    if (!nodeType) return;
    const isBool = nodeType === 'checkbox' || nodeType === 'bool';
    setDraft(d => {
      const needsType = d.value_type !== nodeType;
      // The value is initialised to '' for a fresh action, which is neither
      // null nor a valid bool — seed it to 'true' (the shown default) so True
      // actually persists. Only an explicit 'false' should mean false.
      const needsDefault = isBool && d.value !== 'true' && d.value !== 'false';
      if (!needsType && !needsDefault) return d;
      return {
        ...d,
        ...(needsType ? { value_type: nodeType } : {}),
        ...(needsDefault ? { value: 'true' } : {}),
      };
    });
  }, [nodeType, setDraft]);

  const inputClass = 'bg-app-surface border border-app-border rounded px-2 py-1 text-xs text-app-fg focus:outline-none focus:border-purple-500';

  function renderValueControl() {
    if (isFixedFn || !selectedNode) return null;
    if (nodeType === 'button') {
      return <span className="text-xs text-gray-600 italic">No value — triggers on cue</span>;
    }
    if (nodeType === 'checkbox' || nodeType === 'bool') {
      return (
        <select
          value={draft.value === 'false' ? 'false' : 'true'}
          onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
          className={`${inputClass} w-24`}
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }
    if (nodeType === 'selection') {
      const opts = selectedNode.options || [];
      if (opts.length === 0) {
        // Options didn't come through — fall back to free text and log raw field for diagnosis
        if (selectedNode._rawField) console.warn('Singular Selection field raw data:', selectedNode._rawField);
        return (
          <input
            type="text"
            value={draft.value || ''}
            onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
            placeholder="Value (options unavailable)"
            className={`${inputClass} w-40`}
          />
        );
      }
      // Options are { value, label } (value = the id Singular expects; label =
      // display title). Tolerate the old string shape from any cached nodes.
      return (
        <select
          value={draft.value || ''}
          onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
          className={`${inputClass} max-w-[160px]`}
        >
          <option value="">— Select —</option>
          {opts.map(o => {
            const value = typeof o === 'string' ? o : o.value;
            const label = typeof o === 'string' ? o : o.label;
            return <option key={value} value={value}>{label}</option>;
          })}
        </select>
      );
    }
    if (nodeType === 'number') {
      return (
        <input
          type="number"
          value={draft.value ?? ''}
          onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
          placeholder="Value"
          className={`${inputClass} w-24`}
        />
      );
    }
    if (nodeType === 'json') {
      return (
        <textarea
          value={draft.value || ''}
          onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
          placeholder='{"key": "value"}'
          rows={2}
          className={`${inputClass} w-48 font-mono resize-none`}
        />
      );
    }
    // Default: text
    return (
      <input
        type="text"
        value={draft.value || ''}
        onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
        placeholder="Value"
        className={`${inputClass} w-28`}
      />
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      {/* Sub-composition dropdown */}
      <select
        value={draft.sub_composition_id || ''}
        onChange={e => {
          const comp = singularCompositions.find(c => c.subCompositionId === e.target.value);
          const name = comp?.mainComposition ? 'Overlay Customisation' : (comp?.subCompositionName || e.target.value);
          setDraft(d => ({
            ...d,
            sub_composition_id: e.target.value,
            sub_composition_name: name,
            function: '',
            value: '',
          }));
        }}
        className={`${inputClass} max-w-[160px]`}
      >
        <option value="">— Sub-composition —</option>
        {/* If saved id isn't in list (e.g. compositions not yet loaded), show it as a fallback option */}
        {draft.sub_composition_id && !singularCompositions.find(c => c.subCompositionId === draft.sub_composition_id) && (
          <option value={draft.sub_composition_id}>{draft.sub_composition_name || draft.sub_composition_id} (saved)</option>
        )}
        {[...singularCompositions].sort((a, b) => {
          const nameA = a.mainComposition ? 'Overlay Customisation' : (a.subCompositionName || a.subCompositionId);
          const nameB = b.mainComposition ? 'Overlay Customisation' : (b.subCompositionName || b.subCompositionId);
          return nameA.localeCompare(nameB);
        }).map(c => {
          const id = c.subCompositionId;
          const name = c.mainComposition ? 'Overlay Customisation' : (c.subCompositionName || id);
          return <option key={id} value={id}>{name}</option>;
        })}
      </select>

      {/* Function dropdown */}
      <select
        value={draft.function || ''}
        onChange={e => setDraft(d => ({ ...d, function: e.target.value, value: '' }))}
        className={`${inputClass} max-w-[160px]`}
        disabled={!draft.sub_composition_id}
      >
        <option value="">— Function —</option>
        <option value="IN">IN</option>
        <option value="OUT">OUT</option>
        <option value="hide_all">Hide All</option>
        {/* If saved function is a node ID not in list (e.g. nodes not yet loaded), show as fallback */}
        {draft.function && !['IN', 'OUT', 'hide_all', ''].includes(draft.function) && !nodeOptions.find(n => n.id === draft.function) && !loadingNodes && (
          <option value={draft.function}>{draft.function} (saved)</option>
        )}
        {nodeOptions.length > 0 && (
          <optgroup label="Set field">
            {nodeOptions.map(f => <option key={f.id} value={f.id}>{f.title} ({f.type})</option>)}
          </optgroup>
        )}
        {loadingNodes && <option disabled>Loading fields…</option>}
      </select>

      {/* Value control — type-aware */}
      {renderValueControl()}
    </div>
  );
}

// Sortable wrapper for a single step row
function SortableStep({ id, step, idx, actionsLength, readOnly, onEdit, onClone, onMove, onRemove, cueLookup, vmixInstances }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: readOnly });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 text-base mb-1.5 py-1.5 px-3 bg-app-sub rounded">
      {/* Drag handle */}
      {!readOnly && (
        <div
          {...attributes}
          {...listeners}
          className="text-gray-700 hover:text-gray-500 cursor-grab active:cursor-grabbing flex-shrink-0 px-0.5"
          title="Drag to reorder"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
          </svg>
        </div>
      )}

      {/* Buttons — LEFT side (clone/edit/up/down/delete before the label) */}
      {!readOnly && (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Clone */}
          <button
            onClick={() => onClone(idx)}
            className="text-gray-700 hover:text-green-400 transition-colors"
            title="Clone step"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          {/* Edit */}
          <button
            onClick={() => onEdit(idx)}
            className="text-gray-700 hover:text-blue-400 transition-colors"
            title="Edit step"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          {/* Up */}
          <button
            onClick={() => onMove(idx, -1)}
            disabled={idx === 0}
            className="text-gray-700 hover:text-gray-400 disabled:opacity-30 transition-colors"
            title="Move up"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          {/* Down */}
          <button
            onClick={() => onMove(idx, 1)}
            disabled={idx === actionsLength - 1}
            className="text-gray-700 hover:text-gray-400 disabled:opacity-30 transition-colors"
            title="Move down"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {/* Delete */}
          <button
            onClick={() => onRemove(idx)}
            className="text-gray-700 hover:text-red-500 transition-colors"
            title="Remove step"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <span className={`font-mono flex-1 min-w-0 truncate ${stepBadgeClass(step)}`}>
        {stepLabel(step, cueLookup, vmixInstances)}
      </span>
    </div>
  );
}

// Convert a saved step back to a draft object for editing
function stepToDraft(step) {
  if (step.type === 'delay') return { duration_ms: step.duration_ms ?? 1000 };
  if (step.type === 'vmix') return { function: step.function || '', input: step.input || '', value: step.value || '', target_instance_id: step.target_instance_id || '' };
  if (step.type === 'singular') return {
    sub_composition_id: step.sub_composition_id || '',
    sub_composition_name: step.sub_composition_name || '',
    function: step.function || '',
    value: step.value || '',
    value_type: step.value_type || '',
  };
  if (step.type === 'loop_control') {
    // Migrate legacy 'start'/'stop' to the explicit scheme on edit
    let action = step.action;
    if (action === 'start') action = 'start_bounded';
    else if (action === 'stop') action = 'stop_immediate';
    const allowed = [
      'start_bounded', 'start_infinite',
      'stop_graceful', 'stop_immediate',
      'stop_all_graceful', 'stop_all_immediate',
    ];
    if (!allowed.includes(action)) action = 'stop_immediate';
    return { target_cue_id: step.target_cue_id || '', action };
  }
  return {};
}

// Compact inline test button shown next to LEDs in main row
function InlineTestButton({ showId, cueId }) {
  const [status, setStatus] = useState(null); // null | 'running' | 'ok' | 'err'

  async function handleTest(e) {
    e.stopPropagation();
    setStatus('running');
    try {
      await cuesAPI.test(showId, cueId);
      setStatus('ok');
      setTimeout(() => setStatus(null), 2000);
    } catch {
      setStatus('err');
      setTimeout(() => setStatus(null), 2000);
    }
  }

  return (
    <button
      onClick={handleTest}
      disabled={status === 'running'}
      title={status === 'ok' ? 'Actions fired!' : status === 'err' ? 'Failed' : 'Test actions'}
      className={`w-5 h-5 flex items-center justify-center rounded transition-colors flex-shrink-0 ${
        status === 'ok'  ? 'text-green-400' :
        status === 'err' ? 'text-red-400' :
        'text-gray-500 hover:text-green-400'
      }`}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </button>
  );
}

function CueActionsPanel({ cue, showId, readOnly, vmixInputs, vmixInstances, singularCompositions, onUpdate }) {
  const actions = cue.cue_actions || [];

  // Pull the show's full cue list for the Loop Control target picker
  // (Map<cueId, { label, displayIndex, cue_type }>). Display index = 1-based
  // position in the full cue list, matching the rundown's number column
  // (group children included so subsequent numbers stay in step).
  const allCues = useShowStore(state => state.cues, shallow);
  const cueLookup = React.useMemo(() => {
    const map = new Map();
    for (let i = 0; i < allCues.length; i++) {
      const c = allCues[i];
      map.set(c.id, { label: c.label, displayIndex: i + 1, cue_type: c.cue_type });
    }
    return map;
  }, [allCues]);
  const otherCues = React.useMemo(
    () => allCues.filter(c => c.id !== cue.id),
    [allCues, cue.id],
  );

  // formMode: null = closed | 'add' | 'edit'
  const [formMode, setFormMode] = useState(null);
  const [activeType, setActiveType] = useState(null); // 'vmix' | 'singular' | 'delay' | 'loop_control'
  const [editingIdx, setEditingIdx] = useState(null);
  const [draft, setDraft] = useState({});

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Step IDs for dnd-kit — stable per render based on index
  const stepIds = actions.map((_, i) => `step-${i}`);

  function openAdd(type) {
    let initDraft = {};
    if (type === 'vmix') initDraft = { function: 'ScriptStart', input: '', value: '', target_instance_id: '' };
    else if (type === 'singular') initDraft = { sub_composition_id: '', sub_composition_name: '', function: '', value: '' };
    else if (type === 'delay') initDraft = { duration_ms: 1000 };
    else if (type === 'loop_control') initDraft = { target_cue_id: '', action: 'stop_immediate' };
    setActiveType(type);
    setEditingIdx(null);
    setDraft(initDraft);
    setFormMode('add');
  }

  function openEdit(idx) {
    const step = actions[idx];
    setActiveType(step.type);
    setEditingIdx(idx);
    setDraft(stepToDraft(step));
    setFormMode('edit');
  }

  function closeForm() {
    setFormMode(null);
    setActiveType(null);
    setEditingIdx(null);
    setDraft({});
  }

  function buildStep() {
    if (activeType === 'delay') {
      return { type: 'delay', duration_ms: parseInt(draft.duration_ms) || 0 };
    }
    if (activeType === 'vmix') {
      const fn = (draft.function || '').trim();
      if (!fn) return null;
      const s = { type: 'vmix', function: fn };
      if (draft.input) s.input = draft.input;
      if (draft.value) s.value = draft.value;
      // Omit for Primary (empty) so existing single-instance actions are unchanged.
      if (draft.target_instance_id) s.target_instance_id = draft.target_instance_id;
      return s;
    }
    if (activeType === 'singular') {
      if (!draft.sub_composition_id || !draft.function) return null;
      const s = {
        type: 'singular',
        sub_composition_id: draft.sub_composition_id,
        sub_composition_name: draft.sub_composition_name || draft.sub_composition_id,
        function: draft.function,
      };
      // Checkbox/bool fields default to "true" (the dropdown shows True), so
      // persist that default even when the operator never touched the control —
      // otherwise an untouched True saved no value and only False (which forces
      // a change) ever worked.
      const isBoolField = ['checkbox', 'bool'].includes((draft.value_type || '').toLowerCase());
      // Bool fields are only ever true/false; anything not explicitly 'false'
      // (including the '' a fresh action starts with) means the True default.
      const value = isBoolField ? (draft.value === 'false' ? 'false' : 'true') : draft.value;
      if (value != null && value !== '' && !['IN', 'OUT', 'hide_all'].includes(draft.function)) {
        s.value = value;
        // Carry the field's model type so the backend coerces the value (e.g.
        // checkbox → boolean) before sending to Singular. SingularStepForm keeps
        // draft.value_type in sync with the selected field's type.
        if (draft.value_type) s.value_type = draft.value_type;
      }
      return s;
    }
    if (activeType === 'loop_control') {
      const allowed = [
        'start_bounded', 'start_infinite',
        'stop_graceful', 'stop_immediate',
        'stop_all_graceful', 'stop_all_immediate',
      ];
      const action = allowed.includes(draft.action) ? draft.action : 'stop_immediate';
      const isStopAll = action === 'stop_all_immediate' || action === 'stop_all_graceful';
      if (!isStopAll && !draft.target_cue_id) return null;
      const step = { type: 'loop_control', action };
      if (!isStopAll) step.target_cue_id = draft.target_cue_id;
      return step;
    }
    return null;
  }

  function saveForm() {
    const step = buildStep();
    if (!step) return;
    const arr = [...actions];
    if (formMode === 'edit' && editingIdx !== null) {
      arr[editingIdx] = step;
    } else {
      arr.push(step);
    }
    onUpdate('cue_actions', arr);
    closeForm();
  }

  function cloneStep(idx) {
    const arr = [...actions];
    arr.splice(idx + 1, 0, { ...actions[idx] });
    onUpdate('cue_actions', arr);
  }

  function removeStep(idx) {
    onUpdate('cue_actions', actions.filter((_, i) => i !== idx));
    if (formMode === 'edit' && editingIdx === idx) closeForm();
  }

  function moveStep(idx, dir) {
    const arr = [...actions];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    onUpdate('cue_actions', arr);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = stepIds.indexOf(active.id);
    const newIdx = stepIds.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onUpdate('cue_actions', arrayMove([...actions], oldIdx, newIdx));
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') saveForm();
    if (e.key === 'Escape') closeForm();
    e.stopPropagation();
  }

  const hasActions = actions.length > 0;

  return (
    <div className="px-12 py-4 bg-app-well" onClick={e => e.stopPropagation()}>
      <div className="text-xs text-gray-600 uppercase tracking-wider font-medium mb-2">Actions Timeline</div>

      {!hasActions && !formMode && (
        <p className="text-xs text-gray-700 italic mb-2">No actions configured.</p>
      )}

      {/* Step list with drag-and-drop */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={stepIds} strategy={verticalListSortingStrategy}>
          {actions.map((step, idx) => (
            <React.Fragment key={`step-${idx}`}>
              <SortableStep
                id={`step-${idx}`}
                step={step}
                idx={idx}
                actionsLength={actions.length}
                readOnly={readOnly}
                onEdit={openEdit}
                onClone={cloneStep}
                onMove={moveStep}
                onRemove={removeStep}
                cueLookup={cueLookup}
                vmixInstances={vmixInstances}
              />
              {/* Inline edit form appears directly below the step being edited */}
              {formMode === 'edit' && editingIdx === idx && (
                <div className="mb-1 p-2 bg-app-header rounded border border-blue-800" onKeyDown={handleKeyDown}>
                  <div className="text-[10px] text-blue-500 uppercase tracking-wider font-medium mb-1.5">Editing step {idx + 1}</div>
                  {renderStepForm(activeType, draft, setDraft, vmixInputs, vmixInstances, singularCompositions, showId, otherCues, cueLookup)}
                  <div className="flex gap-2 mt-2">
                    <button onClick={saveForm} className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors">Save</button>
                    <button onClick={closeForm} className="px-2 py-1 text-xs text-gray-500 hover:text-app-fg transition-colors">Cancel</button>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </SortableContext>
      </DndContext>

      {/* Add new step form */}
      {!readOnly && formMode === 'add' && (
        <div className="mt-2 p-2 bg-app-header rounded border border-app-border" onKeyDown={handleKeyDown}>
          {renderStepForm(activeType, draft, setDraft, vmixInputs, vmixInstances, singularCompositions, showId, otherCues, cueLookup)}
          <div className="flex gap-2 mt-2">
            <button onClick={saveForm} className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors">Add</button>
            <button onClick={closeForm} className="px-2 py-1 text-xs text-gray-500 hover:text-app-fg transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Add Step picker */}
      {!readOnly && !formMode && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-700">+ Add step:</span>
          <button onClick={() => openAdd('vmix')} className="text-xs text-blue-500 hover:text-blue-400 transition-colors">vMix</button>
          <span className="text-gray-700">·</span>
          <button onClick={() => openAdd('singular')} className="text-xs text-purple-500 hover:text-purple-400 transition-colors">Singular</button>
          <span className="text-gray-700">·</span>
          <button onClick={() => openAdd('delay')} className="text-xs text-yellow-600 hover:text-yellow-500 transition-colors">Delay</button>
          <span className="text-gray-700">·</span>
          <button onClick={() => openAdd('loop_control')} className="text-xs text-amber-500 hover:text-amber-400 transition-colors">Loop Control</button>
        </div>
      )}

    </div>
  );
}

// Shared form renderer used for both add and edit
function renderStepForm(type, draft, setDraft, vmixInputs, vmixInstances, singularCompositions, showId, otherCues, cueLookup) {
  if (type === 'loop_control') {
    const isStopAll = draft.action === 'stop_all_immediate' || draft.action === 'stop_all_graceful';
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-amber-500 font-mono w-20">Loop</span>
        <select
          value={draft.action}
          onChange={e => setDraft(d => ({ ...d, action: e.target.value }))}
          className="bg-app-surface border border-app-border rounded px-2 py-1 text-xs text-app-fg focus:outline-none focus:border-amber-500"
        >
          <option value="start_bounded">Start loop</option>
          <option value="start_infinite">Start infinite loop</option>
          <option value="stop_graceful">Stop after current iteration</option>
          <option value="stop_immediate">Stop immediately</option>
          <option value="stop_all_graceful">Stop ALL after current iteration</option>
          <option value="stop_all_immediate">Stop ALL immediately</option>
        </select>
        {isStopAll ? (
          <span className="text-xs text-gray-500 italic">Applies to every running loop</span>
        ) : (
          <select
            value={draft.target_cue_id}
            onChange={e => setDraft(d => ({ ...d, target_cue_id: e.target.value }))}
            autoFocus
            className="flex-1 min-w-[200px] bg-app-surface border border-app-border rounded px-2 py-1 text-xs text-app-fg focus:outline-none focus:border-amber-500"
          >
            <option value="">— Pick a cue —</option>
            {(otherCues || []).map(c => {
              const info = cueLookup?.get(c.id);
              const idx = info?.displayIndex;
              const prefix = idx ? `${idx}. ` : '';
              const label = c.label || 'untitled';
              return (
                <option key={c.id} value={c.id}>{prefix}{c.cue_type} — {label}</option>
              );
            })}
          </select>
        )}
      </div>
    );
  }
  if (type === 'delay') {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-yellow-600 font-mono w-12">Delay</span>
        <input
          type="number"
          value={draft.duration_ms}
          onChange={e => setDraft(d => ({ ...d, duration_ms: e.target.value }))}
          placeholder="ms"
          autoFocus
          className="bg-app-surface border border-app-border rounded px-2 py-1 text-xs text-app-fg w-24 focus:outline-none focus:border-yellow-500"
        />
        <span className="text-xs text-gray-600">ms</span>
      </div>
    );
  }
  if (type === 'vmix') {
    return (
      <VmixStepForm
        draft={draft}
        setDraft={setDraft}
        vmixInputs={vmixInputs}
        vmixInstances={vmixInstances}
        showId={showId}
      />
    );
  }
  if (type === 'singular') {
    return (
      <SingularStepForm
        showId={showId}
        draft={draft}
        setDraft={setDraft}
        singularCompositions={singularCompositions}
      />
    );
  }
  return null;
}

const CueRow = React.memo(function CueRow({ cue, showId, index, displayIndex, displayNumber, isOnAir, isNext, readOnly = false, textOnly = false, inTime, labelColWidth = 200, isSelected = false, onToggleSelect, isPreShow = false, isShowStart = false, onDeleteSelected, visibleCols = {}, colWidths = {}, isExpanded = false, onToggleExpand, isGroupParent = false, isChild = false, isGroupCollapsed = false, onToggleGroupCollapse, childrenCount = 0, isDropTarget = false, isContainerLocked = false, groupDuration = undefined, isCollapsing = false, isExpanding = false }) {
  const updateCue = useShowStore(s => s.updateCue);
  // textOnly: a guest without production access. Script, notes and graphics text
  // stay editable; running order, timing, type, label and automation are locked,
  // matching what the server will accept.
  const lockStructure = readOnly || textOnly;
  const typeAccent = useSettingsStore(s => s.typeAccent);
  const relativeTimes = useSettingsStore(s => effectiveRelativeTimes(s.relativeTimes, s.uiStyle));
  const actionsRunning = useShowStore(s => !!s.runningActions[cue.id]);
  const vmixInputs = useConnectionStore(s => s.vmixInputs, shallow);
  const vmixInstances = useConnectionStore(s => s.vmixInstances, shallow);
  const singularCompositions = useConnectionStore(s => s.singularCompositions, shallow);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const typeMenuRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [showScriptEditor, setShowScriptEditor] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: cue.id, disabled: lockStructure || isOnAir });

  // For GROUP rows, expose an explicit non-sortable droppable that occupies the
  // middle 50% of the row. This gives "drop INTO this group" a stable target
  // that doesn't shift as sortable animates the surrounding rows. Top/bottom
  // 25% strips fall through to the outer sortable for sort-above / sort-below.
  const groupInner = useDroppable({
    id: `${cue.id}:inner`,
    disabled: !isGroupParent || lockStructure || isOnAir || isDragging,
    data: { type: 'group-inner', groupId: cue.id },
  });

  // Freeze a GROUP row in place for the duration of any non-GROUP drag —
  // it's a container, not a sortable item in that gesture. Sortable strategy
  // would otherwise slide the row up/down as the cursor approaches and only
  // the inner-droppable freeze (isDropTarget) would kick in once the cursor
  // reached the middle band, giving an awkward "slip out, then snap back"
  // feel. With container-lock applied for the whole drag, the GROUP stays
  // anchored end-to-end. Keeping `transition` set means once the drag ends
  // and the lock releases, any catch-up movement still animates smoothly.
  const freezeForDrop = isGroupParent && (isDropTarget || isContainerLocked);
  const style = {
    transform: freezeForDrop ? undefined : CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  useEffect(() => {
    if (!showTypeMenu) return;
    function handleOutsideClick(e) {
      if (typeMenuRef.current && !typeMenuRef.current.contains(e.target)) {
        setShowTypeMenu(false);
      }
    }
    window.addEventListener('mousedown', handleOutsideClick);
    return () => window.removeEventListener('mousedown', handleOutsideClick);
  }, [showTypeMenu]);

  const handleUpdate = useCallback(async (field, value) => {
    if (readOnly || (textOnly && !TEXT_FIELDS.includes(field))) return;
    setSaving(true);
    try {
      const updated = await cuesAPI.update(showId, cue.id, { [field]: value });
      updateCue(updated.data);
    } catch (err) {
      console.error('Failed to update cue:', err);
    } finally {
      setSaving(false);
    }
  }, [showId, cue.id, readOnly, textOnly, updateCue]);

  async function handleClone() {
    try {
      await cuesAPI.clone(showId, cue.id);
    } catch (err) {
      console.error('Failed to clone cue:', err);
    }
  }

  async function handleDelete() {
    if (isSelected && onDeleteSelected) {
      onDeleteSelected();
      return;
    }
    if (!confirm(`Delete cue "${cue.label}"?`)) return;
    try {
      await cuesAPI.delete(showId, cue.id);
    } catch (err) {
      console.error('Failed to delete cue:', err);
    }
  }

  // Computed timing values
  const outTime = inTime ? new Date(inTime.getTime() + (cue.planned_duration || 0) * 1000) : null;


  const isDisabled = cue.enabled === false;
  const isComplete = cue.status === 'complete';
  const isSkipped = cue.status === 'skipped';
  const isHistory = isComplete || isSkipped;
  const dimmed = isHistory || isDisabled;

  // §3b type scale + receding action icons. Sizes/weights sampled from the
  // handoff mockup: the eye should land on label → IN → DUR, never the icon
  // strip. Action clusters recede by state and snap to full opacity on hover.
  const labelFontPx = isOnAir ? 16 : isNext ? 15 : 14;
  // Weight discipline: 600 for the on-air label ONLY; every other label is 400
  // (next/group emphasis comes from the amber tint + size, not weight).
  const labelWeight = isOnAir ? 600 : 400;
  const inFontPx = (isOnAir || isNext) ? 15 : 14;
  // Timecode is always 400 — emphasis via size/colour, never weight.
  const inWeight = 400;
  // An armed action list always runs to completion, so a cue can still be firing
  // after the show has moved on and it has become history. While that is true the
  // cluster stays at full opacity — otherwise the "still working" blink plays at
  // 35% and reads as finished the instant the next cue is taken.
  const actClusterClass =
    (actionsRunning ? 'opacity-100'
      : isHistory ? 'opacity-[.35]'
      : isDisabled ? 'opacity-40'
      : (isOnAir || isNext) ? 'opacity-100'
      : 'opacity-[.65]') + ' group-hover:opacity-100 transition-opacity';

  // Inner row classes — row beds assigned by role: upcoming rows sit on the
  // dark --c-well, past/done rows on the slightly-lighter --c-row-past, so the
  // list reads as content below the chrome. (Ordering: well L5 < row-past L7 <
  // hover L9.) Per-type hover tints from CUE_TYPE_ROW_TINT stay untouched.
  // min-height, not just content height: the drag handle and expand toggle carry
  // h-12 and were what set the row's height. With edit controls hidden they are
  // gone, and rows collapsed to the height of their text — the whole list
  // changed shape. Pin it here so height never depends on which cells render.
  let innerRowClass = 'group flex items-center gap-0 select-none min-h-[3rem]';
  if (isOnAir) {
    innerRowClass += ' cue-row-on-air';
  } else if (isDisabled) {
    innerRowClass += ' bg-app-well opacity-60';
  } else if (cue.status === 'complete') {
    innerRowClass += ' cue-row-complete bg-app-row-past';
  } else if (cue.status === 'skipped') {
    innerRowClass += ' cue-row-skipped bg-app-row-past';
  } else if (cue.cue_type === 'SECTION_BREAK' || isShowStart) {
    // SHOW_START ("GO") shares SECTION_BREAK's loud white-band styling so
    // the show's start point is unmissable in a long rundown.
    innerRowClass += ' bg-white hover:bg-gray-100';
  } else if (isGroupParent) {
    innerRowClass += isDropTarget
      ? ' bg-amber-500/40 border-l-4 border-amber-300 ring-2 ring-inset ring-amber-300 shadow-[0_0_0_3px_rgba(252,211,77,0.45),inset_0_0_24px_rgba(252,211,77,0.25)] transition-colors duration-100'
      : ' bg-amber-950/30 hover:bg-amber-950/50 border-l-2 border-amber-700/60 transition-colors duration-150';
  } else if (isChild) {
    innerRowClass += ' bg-app-well' + (CUE_TYPE_ROW_TINT[cue.cue_type] || ' hover:bg-app-panel') + ' border-l-2 border-amber-800/40 ml-0';
  } else if (isPreShow) {
    innerRowClass += ' bg-app-well hover:bg-app-row-hover';
  } else {
    innerRowClass += ' bg-app-well' + (CUE_TYPE_ROW_TINT[cue.cue_type] || ' hover:bg-app-panel');
  }
  // NEXT is a flag, not a row state: additive, so the row keeps whatever its
  // type/status already gives it (white divider band, group tint, type hover)
  // and merely gains the amber left bar. It used to be a branch in the chain
  // above, replacing the row's own styling with an amber wash that read as live.
  if (isNext && !isOnAir && !isDisabled) innerRowClass += ' cue-row-next';
  if (isSelected) innerRowClass += ' ring-1 ring-inset ring-blue-500/60';
  if (isExpanding) innerRowClass += ' animate-group-enter';
  if (isCollapsing) innerRowClass += ' animate-group-exit pointer-events-none';

  // Type-coloured outline around the Label cell — applied on rows that
  // don't already carry their own dominant visual state (on-air red,
  // complete/skipped grey-out, GROUP parent, SHOW_START, etc). The NEXT
  // cue keeps the outline because that's when knowing the cue type
  // matters most. Group children also get the outline so type stays
  // identifiable inside a group.
  const showLabelOutline = !isOnAir && !isDisabled
    && cue.status !== 'complete' && cue.status !== 'skipped'
    && cue.cue_type !== 'SECTION_BREAK' && !isGroupParent && !isShowStart;
  // 'outline' (default): box around the label. 'edge' (A/B trial): no box —
  // the type colour becomes a left bar + light tint on the row instead.
  const labelOutlineClass = (typeAccent === 'outline' && showLabelOutline)
    ? (CUE_TYPE_LABEL_OUTLINE[cue.cue_type] || '')
    : '';
  // Resting tint inside the box. On rows that pick up a coloured hover tint
  // (pending + child — see CUE_TYPE_ROW_TINT), group-hover clears the box's own
  // fill so the row tint shows through uniformly instead of doubling up behind
  // the box. The next row is no longer excluded — it now behaves like any other
  // pending row and takes the same coloured hover.
  const rowTakesTypeHover = showLabelOutline && !isPreShow;
  const labelFillClass = (typeAccent === 'outline' && showLabelOutline && CUE_TYPE_LABEL_FILL[cue.cue_type])
    ? `${CUE_TYPE_LABEL_FILL[cue.cue_type]}${rowTakesTypeHover ? ' group-hover:bg-transparent' : ''}`
    : '';

  // Row-edge type accent — the SAME format as the live highlight (4px left bar
  // + tint gradient), just in the cue's type colour. Applied only where the
  // label outline would have coloured a row AND it doesn't already own a status
  // edge (skips on-air/next, SEGMENT, groups, dividers, history, disabled).
  // Next is still excluded so the amber flag owns the left edge unshared.
  const typeEdgeToken = (typeAccent === 'edge' && showLabelOutline && !isNext
    && cue.cue_type !== 'SEGMENT')
    ? TYPE_META[cue.cue_type]?.token
    : null;
  const rowAccentStyle = typeEdgeToken ? {
    boxShadow: `inset 0 0 0 1.5px var(${typeEdgeToken}), inset 4px 0 0 var(${typeEdgeToken})`,
    backgroundImage: `linear-gradient(90deg, color-mix(in srgb, var(${typeEdgeToken}) 24%, transparent) 0%, color-mix(in srgb, var(${typeEdgeToken}) 12%, transparent) 55%, color-mix(in srgb, var(${typeEdgeToken}) 9%, transparent) 100%)`,
  } : undefined;

  // SECTION_BREAK and SHOW_START share the loud white-band styling so the
  // show's start and section dividers both read the same way.
  const isShowDivider = cue.cue_type === 'SECTION_BREAK' || isShowStart;
  // No longer excludes the next cue: a divider that happens to be on deck keeps
  // its white band and just picks up the amber left bar, rather than switching
  // to amber-on-dark and looking like a different kind of row.
  const isSectionBreak = isShowDivider && !isOnAir && !dimmed;
  const textClass = isOnAir ? 'text-app-fg'
    // Past/done/disabled label — gray-500 (was gray-600) to clear 3:1 on the
    // dark --c-row-past / --c-well bed.
    : dimmed ? 'text-gray-500'
    : isSectionBreak ? 'text-black font-medium'
    : 'text-gray-300';
  const monoClass = isOnAir ? 'text-app-fg/80 font-mono'
    // Past/done/disabled meta/timecodes — gray-500 (was gray-700, 1.92:1).
    : dimmed ? 'text-gray-500 font-mono'
    : isSectionBreak ? 'text-black/70 font-mono font-medium'
    : 'text-gray-500 font-mono';

  const hasActions = (cue.cue_actions || []).length > 0;
  const showExpandToggle = !readOnly || hasActions;
  // Leading-cell toggles from the column menu. Default on, so a stored pref set
  // before these existed still shows them.
  const showEditControls = visibleCols.editControls !== false;
  const showStatusCol = visibleCols.status !== false;

  return (
    <div ref={setNodeRef} style={style} className={`relative border-b border-app-line${isSectionBreak ? ' bg-white' : ''}`}>
      {isGroupParent && (
        <div
          ref={groupInner.setNodeRef}
          aria-hidden="true"
          className="absolute left-0 right-0 pointer-events-none"
          style={{ top: '25%', height: '50%', zIndex: 1 }}
        />
      )}
      <div className={innerRowClass} style={rowAccentStyle}>

        {/* Child indent spacer — sits before handle so both handle and content are indented */}
        {isChild && <div className="w-6 flex-shrink-0" />}

        {/* Edit controls — drag, expand, enable/clone/delete, select. Hidden as
            one group via the column menu, leaving a w-12 gutter that mirrors the
            header's (which still holds the column menu itself). */}
        {showEditControls ? (
        <>
        {/* Drag Handle */}
        {!lockStructure && !isOnAir ? (
          <div
            {...attributes}
            {...listeners}
            {...noMouseFocus}
            className={`w-8 flex-shrink-0 flex items-center justify-center h-12 text-gray-500 hover:text-gray-300 cursor-grab active:cursor-grabbing ${actClusterClass}`}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
            </svg>
          </div>
        ) : (
          <div className="w-8 flex-shrink-0" />
        )}

        {/* Expand toggle — LEFT SIDE (group collapse for GROUP type, actions expand otherwise) */}
        {isGroupParent ? (
          <button
            onClick={() => onToggleGroupCollapse?.(cue.id)}
            className="w-8 flex-shrink-0 flex items-center justify-center h-12 text-amber-500 hover:text-amber-300 transition-colors rounded"
            title={isGroupCollapsed ? `Expand group (${childrenCount} cue${childrenCount !== 1 ? 's' : ''})` : 'Collapse group'}
          >
            <svg
              className={`w-4 h-4 transition-transform ${isGroupCollapsed ? '-rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        ) : showExpandToggle ? (
          <button
            onClick={() => onToggleExpand?.(cue.id)}
            className={`w-8 flex-shrink-0 flex items-center justify-center h-12 transition-colors rounded ${
              hasActions
                ? 'text-blue-400 hover:text-blue-300'
                : 'text-gray-500 hover:text-gray-300'
            }`}
            title={isExpanded ? 'Collapse actions' : 'Actions timeline'}
          >
            <svg
              className={`w-4 h-4 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        ) : (
          <div className="w-8 flex-shrink-0" />
        )}

        {/* Enable/Disable + Clone + Delete — LEFT SIDE */}
        {!lockStructure ? (
          <div className={`w-20 flex-shrink-0 flex items-center ${actClusterClass}`}>
            <button
              onClick={e => { e.stopPropagation(); handleUpdate('enabled', isDisabled); }}
              className={`p-1 transition-colors rounded ${isDisabled ? 'text-gray-600 hover:text-green-400' : 'text-green-500 hover:text-gray-400'}`}
              title={isDisabled ? 'Enable cue' : 'Disable cue (actions will be skipped)'}
            >
              {/* Power / circle icon */}
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 5.636a9 9 0 1012.728 0M12 3v9" />
              </svg>
            </button>
            <button
              onClick={handleClone}
              className="p-1 text-gray-400 hover:text-blue-400 transition-colors rounded"
              title="Clone cue"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              onClick={handleDelete}
              className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded"
              title="Delete cue"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="w-20 flex-shrink-0" />
        )}

        {/* Checkbox — supports shift-click for range selection */}
        {!lockStructure ? (
          <div
            className={`w-6 flex-shrink-0 flex items-center justify-center ${actClusterClass}`}
            onClick={e => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => {}}
              onClick={e => { e.stopPropagation(); onToggleSelect?.(cue.id, e.shiftKey); }}
              className="w-4 h-4 cursor-pointer accent-blue-500"
              tabIndex={-1}
            />
          </div>
        ) : (
          <div className="w-6 flex-shrink-0" />
        )}
        </>
        ) : isGroupParent ? (
          // Groups keep their collapse chevron even with edit controls hidden —
          // without it a collapsed group could only be reopened via collapse-all
          // in the header. Absolutely positioned so it costs no layout width and
          // every row still starts flush left at the same x.
          <button
            onClick={() => onToggleGroupCollapse?.(cue.id)}
            className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center text-amber-500 hover:text-amber-300 transition-colors z-[2]"
            title={isGroupCollapsed ? `Expand group (${childrenCount} cue${childrenCount !== 1 ? 's' : ''})` : 'Collapse group'}
          >
            <svg
              className={`w-4 h-4 transition-transform ${isGroupCollapsed ? '-rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        ) : null}

        {/* Status — ON AIR pill / NEXT pill / green ✓ / grey ». Moved here from
            the far right so the marker sits beside the cue it describes. */}
        {showStatusCol && (
          // pl-6 only where the overlaid group chevron would otherwise sit on
          // top of the marker — the cell keeps its width, so columns stay aligned.
          <div className={`w-20 flex-shrink-0 flex justify-center${!showEditControls && isGroupParent ? ' pl-6' : ''}`}>
            {isOnAir && (
              <span
                className="on-air-pulse font-medium uppercase rounded inline-flex items-center gap-1.5"
                style={{ fontSize: '10px', letterSpacing: '.06em', padding: '3px 10px', background: 'var(--live)', color: '#fff' }}
              >
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff' }} />
                ON AIR
              </span>
            )}
            {isNext && !isOnAir && (
              <span
                className="font-medium uppercase rounded"
                style={{ fontSize: '10px', letterSpacing: '.06em', padding: '3px 10px', border: '1px solid var(--next)', color: 'var(--next)' }}
              >
                NEXT
              </span>
            )}
            {isComplete && (
              <svg className="w-5 h-5" style={{ color: 'var(--green)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {isSkipped && (
              <span style={{ color: 'var(--faint)', fontSize: '18px', fontWeight: 500, lineHeight: 1 }}>»</span>
            )}
          </div>
        )}

        {/* Order number — script-style hierarchical (e.g. 2, 2.1, 2.2) */}
        <div className={`w-14 text-center font-mono ${isChild ? 'text-[13px]' : 'text-[14px]'} ${isOnAir ? 'text-app-fg/70' : 'text-gray-500'} flex-shrink-0`}>
          {displayNumber ?? index + 1}
        </div>

        {/* Colour bar */}
        <div
          className="w-1 self-stretch flex-shrink-0 mr-3"
          style={{ backgroundColor: cue.colour || 'transparent' }}
        />

        {/* Action LEDs + loop toggle + inline Test button */}
        {(() => {
          const hasVmix = (cue.cue_actions || []).some(a => a.type === 'vmix');
          const hasSingular = (cue.cue_actions || []).some(a => a.type === 'singular');
          // While the cue is working through its list, blink the lit dots so an
          // operator can see actions are still firing before taking the next cue.
          const vmixDot = `w-3 h-3 rounded-full flex-shrink-0${actionsRunning && hasVmix ? ' led-running' : ''}`;
          const singularDot = `w-3 h-3 rounded-full flex-shrink-0${actionsRunning && hasSingular ? ' led-running' : ''}`;
          // Filled (green vMix / violet Singular) when actions exist, hollow
          // surface3 when not, invisible on history rows — per §3b. Exception:
          // a history row that is STILL firing keeps its lit dots, or the blink
          // would animate a transparent circle and look extinguished the moment
          // the next cue is taken.
          const dotsHidden = isHistory && !actionsRunning;
          const vmixBg = dotsHidden ? 'transparent' : hasVmix ? 'var(--green)' : 'var(--surface3)';
          const singularBg = dotsHidden ? 'transparent' : hasSingular ? 'var(--violet)' : 'var(--surface3)';
          return (
            <div className={`w-24 flex-shrink-0 flex items-center gap-1.5 ${actClusterClass}`}>
              <span
                className={vmixDot}
                style={{ backgroundColor: vmixBg }}
                title={hasVmix ? (actionsRunning ? 'Running vMix actions…' : 'Has vMix actions') : 'No vMix actions'}
              />
              <span
                className={singularDot}
                style={{ backgroundColor: singularBg }}
                title={hasSingular ? (actionsRunning ? 'Running Singular actions…' : 'Has Singular actions') : 'No Singular actions'}
              />
              {!lockStructure && hasActions && (() => {
                const mode = cue.loop_mode || 'off';
                const next = mode === 'off' ? 'bounded' : mode === 'bounded' ? 'infinite' : 'off';

                // If this cue contains any loop_control steps, override the
                // icon colour to flag what it does to OTHER cues' loops.
                // Stop precedence > start (stops are usually higher-stakes
                // when scanning a rundown). Mixed (start + stop on different
                // targets) shows yellow.
                const lcSteps = (cue.cue_actions || []).filter(a => a.type === 'loop_control');
                const hasStart = lcSteps.some(a =>
                  a.action === 'start_bounded' || a.action === 'start_infinite' || a.action === 'start'
                );
                const hasStop = lcSteps.some(a =>
                  a.action === 'stop_immediate' || a.action === 'stop_graceful' || a.action === 'stop' ||
                  a.action === 'stop_all_immediate' || a.action === 'stop_all_graceful'
                );

                let colourClass;
                if (hasStop && hasStart) colourClass = 'text-yellow-400';
                else if (hasStop) colourClass = 'text-red-500';
                else if (hasStart) colourClass = 'text-green-400';
                else colourClass = mode === 'off' ? 'text-gray-600 hover:text-amber-400' : 'text-amber-400';

                let title = mode === 'off'
                  ? 'Loop OFF — click to enable bounded loop'
                  : mode === 'bounded'
                    ? 'Bounded loop (stops on next cue) — click to make infinite'
                    : 'Infinite loop (persists across cues) — click to disable';
                if (hasStart && hasStop) title = `Loop control: starts AND stops other cues' loops · ${title}`;
                else if (hasStop) title = `Loop control: stops another cue's loop · ${title}`;
                else if (hasStart) title = `Loop control: starts another cue's loop · ${title}`;

                return (
                  <button
                    onClick={e => { e.stopPropagation(); handleUpdate('loop_mode', next); }}
                    title={title}
                    className={`relative w-5 h-5 flex items-center justify-center rounded transition-colors flex-shrink-0 ${colourClass}`}
                  >
                    {/* Loop / repeat icon */}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {mode === 'infinite' && (
                      <span className="absolute -bottom-1 -right-1 text-[9px] leading-none font-medium bg-app-bg px-0.5 rounded">
                        ∞
                      </span>
                    )}
                  </button>
                );
              })()}
              {!lockStructure && hasActions && (
                <InlineTestButton showId={showId} cueId={cue.id} />
              )}
            </div>
          );
        })()}

        {/* Cue Type */}
        {visibleCols.type !== false && (
          <div className="cue-type-cell flex-shrink-0 px-1 text-center" style={{ width: colWidths.type || 80 }}>
            {lockStructure ? (
              <CueTypeBadge type={cue.cue_type} compact />
            ) : (
              <div className="relative" ref={typeMenuRef}>
                <button onClick={() => setShowTypeMenu(!showTypeMenu)} className="text-left">
                  <CueTypeBadge type={cue.cue_type} compact />
                </button>
                {showTypeMenu && (
                  <div className="absolute top-full left-0 mt-1 bg-app-surface border border-app-border-strong rounded shadow-xl z-20 py-1 min-w-[120px]">
                    {CUE_TYPES.map(type => (
                      <button
                        key={type}
                        onClick={() => { handleUpdate('cue_type', type); setShowTypeMenu(false); }}
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-app-border transition-colors ${cue.cue_type === type ? 'text-app-fg' : 'text-gray-400'}`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* IN time — the primary timecode; slightly larger on live+next. Not
            tinted amber on next: colour inside the row was part of what made an
            on-deck cue read as the live one. The left bar carries that now. */}
        {visibleCols.in !== false && (
          <div className="flex-shrink-0 px-1 text-center" style={{ width: colWidths.in || 80 }}>
            <InTime
              inTime={inTime}
              relative={relativeTimes && !isOnAir && !isHistory}
              className={`${monoClass} whitespace-nowrap`}
              style={{ fontSize: `${inFontPx}px`, fontWeight: inWeight }}
            />
          </div>
        )}

        {/* Label — wraps rather than truncating, so a multiline label shows every
             line and the row grows to fit it (the virtualiser measures real row
             heights, so tall rows scroll correctly). */}
        <div className={`flex-shrink-0 px-2 flex items-center gap-2 ${labelOutlineClass} ${labelFillClass}`} style={{ width: labelColWidth }}>
          {lockStructure ? (
            <span
              className={`block flex-1 whitespace-pre-wrap break-words ${textClass}`}
              style={{ fontSize: `${labelFontPx}px`, fontWeight: labelWeight }}
            >{cue.label}</span>
          ) : (
            <EditableCell
              value={cue.label}
              onSave={(v) => handleUpdate('label', v)}
              placeholder="Label..."
              multiline
              className={`w-full whitespace-pre-wrap break-words ${textClass}`}
              style={{ fontSize: `${labelFontPx}px`, fontWeight: labelWeight }}
              labelIndex={displayIndex}
              onArrowDown={() => {
                const next = document.querySelector(`[data-cue-label-trigger="${displayIndex + 1}"]`);
                if (next) next.click();
              }}
              onArrowUp={() => {
                const prev = document.querySelector(`[data-cue-label-trigger="${displayIndex - 1}"]`);
                if (prev) prev.click();
              }}
            />
          )}
          {isGroupParent && childrenCount > 0 && !isDropTarget && (
            <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] bg-amber-900/40 text-amber-500 border border-amber-800/60 font-mono">
              {isGroupCollapsed ? `${childrenCount}▾` : childrenCount}
            </span>
          )}
          {isDropTarget && (
            <span className="flex-shrink-0 px-2.5 py-1 rounded text-[11px] bg-amber-400 text-amber-950 border border-amber-200 font-medium uppercase tracking-wide shadow-[0_0_12px_rgba(252,211,77,0.7)] animate-pulse">
              + Add to group
            </span>
          )}
        </div>

        {/* Duration (HH:MM:SS) */}
        {visibleCols.duration !== false && (
          <div className="text-center px-1 flex-shrink-0" style={{ width: colWidths.duration || 80 }}>
            {(lockStructure || isGroupParent) ? (
              <span className={`text-base ${monoClass}`} style={{ fontWeight: undefined }} title={isGroupParent ? 'Sum of child durations' : undefined}>
                {formatHMS(isGroupParent ? (groupDuration ?? 0) : cue.planned_duration)}
              </span>
            ) : (
              <EditableCell
                value={formatHMS(cue.planned_duration)}
                onSave={(v) => handleUpdate('planned_duration', Math.max(0, parseHMS(v)))}
                placeholder="00:00:00"
                className={`text-base font-mono text-center ${textClass}`}
                style={{ fontWeight: undefined }}
                durationIndex={displayIndex}
                onArrowUp={() => {
                  const prev = document.querySelector(`[data-cue-duration-trigger="${displayIndex - 1}"]`);
                  if (prev) prev.click();
                }}
                onArrowDown={() => {
                  const next = document.querySelector(`[data-cue-duration-trigger="${displayIndex + 1}"]`);
                  if (next) next.click();
                }}
              />
            )}
          </div>
        )}

        {/* OUT time — derived, so it reads last: 13px, faint */}
        {visibleCols.out !== false && (
          <div className="flex-shrink-0 px-1 text-center" style={{ width: colWidths.out || 80 }}>
            <span className={monoClass} style={{ fontSize: '14px' }}>{formatClockTime(outTime)}</span>
          </div>
        )}

        {/* +/- (ahead/behind) */}
        {visibleCols.plusMinus !== false && (
          <div className="flex-shrink-0 px-1 text-center" style={{ width: colWidths.plusMinus || 96 }}>
            <PlusMinusCell inTime={inTime} status={cue.status} />
          </div>
        )}

        {/* Script (truncated preview — click to open rich editor) */}
        {visibleCols.script !== false && (
          <div className="flex-shrink-0 px-2 overflow-hidden" style={{ width: colWidths.script || 180 }}>
            <span
              onClick={() => setShowScriptEditor(true)}
              className="text-base text-gray-500 hover:text-gray-300 truncate block cursor-pointer transition-colors"
              title="Click to edit script"
            >
              {stripHtml(cue.script) || <span className="text-gray-700 italic">Script…</span>}
            </span>
          </div>
        )}

        {/* Top Right Logo */}
        {visibleCols.topRightLogo !== false && (
          <div className="flex-shrink-0 px-2 overflow-hidden" style={{ width: colWidths.topRightLogo || 110 }}>
            {readOnly ? (
              <span className={`text-base truncate block ${textClass}`}>{cue.top_right_logo || ''}</span>
            ) : (
              <EditableCell
                value={cue.top_right_logo || ''}
                onSave={v => handleUpdate('top_right_logo', v || null)}
                placeholder="Top right…"
                className={`text-base w-full truncate ${textClass}`}
                triggerData={{ 'data-cue-trlogo-trigger': displayIndex }}
                onArrowDown={() => {
                  const next = document.querySelector(`[data-cue-trlogo-trigger="${displayIndex + 1}"]`);
                  if (next) next.click();
                }}
                onArrowUp={() => {
                  const prev = document.querySelector(`[data-cue-trlogo-trigger="${displayIndex - 1}"]`);
                  if (prev) prev.click();
                }}
              />
            )}
          </div>
        )}

        {/* Bottom Right Logo */}
        {visibleCols.bottomRightLogo !== false && (
          <div className="flex-shrink-0 px-2 overflow-hidden" style={{ width: colWidths.bottomRightLogo || 110 }}>
            {readOnly ? (
              <span className={`text-base truncate block ${textClass}`}>{cue.bottom_right_logo || ''}</span>
            ) : (
              <EditableCell
                value={cue.bottom_right_logo || ''}
                onSave={v => handleUpdate('bottom_right_logo', v || null)}
                placeholder="Btm right…"
                className={`text-base w-full truncate ${textClass}`}
                triggerData={{ 'data-cue-brlogo-trigger': displayIndex }}
                onArrowDown={() => {
                  const next = document.querySelector(`[data-cue-brlogo-trigger="${displayIndex + 1}"]`);
                  if (next) next.click();
                }}
                onArrowUp={() => {
                  const prev = document.querySelector(`[data-cue-brlogo-trigger="${displayIndex - 1}"]`);
                  if (prev) prev.click();
                }}
              />
            )}
          </div>
        )}

        {/* Other Graphics */}
        {visibleCols.otherGraphics !== false && (
          <div className="flex-shrink-0 px-2 overflow-hidden" style={{ width: colWidths.otherGraphics || 100 }}>
            {readOnly ? (
              <span className={`text-base truncate block ${textClass}`}>{cue.other_graphics || ''}</span>
            ) : (
              <EditableCell
                value={cue.other_graphics || ''}
                onSave={v => handleUpdate('other_graphics', v || null)}
                placeholder="Graphics…"
                className={`text-base w-full truncate ${textClass}`}
                triggerData={{ 'data-cue-othergfx-trigger': displayIndex }}
                onArrowDown={() => {
                  const next = document.querySelector(`[data-cue-othergfx-trigger="${displayIndex + 1}"]`);
                  if (next) next.click();
                }}
                onArrowUp={() => {
                  const prev = document.querySelector(`[data-cue-othergfx-trigger="${displayIndex - 1}"]`);
                  if (prev) prev.click();
                }}
              />
            )}
          </div>
        )}

        {/* Notes */}
        {visibleCols.notes !== false && (
          <div className="flex-shrink-0 px-2 overflow-hidden" style={{ width: colWidths.notes || 100 }}>
            {readOnly ? (
              <span className={`text-base truncate block ${textClass}`}>{cue.notes || ''}</span>
            ) : (
              <EditableCell
                value={cue.notes || ''}
                onSave={v => handleUpdate('notes', v || null)}
                placeholder="Notes…"
                className={`text-base w-full truncate ${textClass}`}
                triggerData={{ 'data-cue-notes-trigger': displayIndex }}
                onArrowDown={() => {
                  const next = document.querySelector(`[data-cue-notes-trigger="${displayIndex + 1}"]`);
                  if (next) next.click();
                }}
                onArrowUp={() => {
                  const prev = document.querySelector(`[data-cue-notes-trigger="${displayIndex - 1}"]`);
                  if (prev) prev.click();
                }}
              />
            )}
          </div>
        )}

      </div>

      {/* Expanded actions panel */}
      {isExpanded && (
        <div className="border-t border-app-line">
          <CueActionsPanel
            cue={cue}
            showId={showId}
            readOnly={lockStructure}
            vmixInputs={vmixInputs}
            vmixInstances={vmixInstances}
            singularCompositions={singularCompositions}
            onUpdate={handleUpdate}
          />
          {/* Collapse from the bottom — so you can close a long actions list
              after scrolling to its end without scrolling back to the top. */}
          <button
            onClick={() => onToggleExpand?.(cue.id)}
            title="Collapse actions"
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs text-gray-500 hover:text-gray-300 hover:bg-app-header transition-colors border-t border-app-line"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
            Collapse
          </button>
        </div>
      )}

      {/* Script rich-text editor modal */}
      {showScriptEditor && (
        <ScriptEditor
          value={cue.script}
          onSave={(html) => handleUpdate('script', html)}
          onClose={() => setShowScriptEditor(false)}
        />
      )}
    </div>
  );
});

export default CueRow;
